import { ApiResponse, OpsType } from '../../types';
import { apiClient } from '../client';

export interface RomaneioCadastroView {
  dbName: string;
  ops: OpsType;
}

export interface RomaneioCheckoutSummary {
  dbName: string;
  ops?: OpsType;
}

export interface RomaneioCheckoutSubmit {
  dbName: string;
  ops: OpsType;
  checkoutDate: string;
  userInfo: { email: string };
}

export interface RomaneioStatusPerson {
  numeroDocumento: string;
  dbName: string;
  ops?: OpsType;
}

export interface RomaneioTEDDownload {
  filterDate: string;
  type: string;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioPaymentSummary {
  filterDate: string;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioStatistics {
  filterDate: string;
  type: string;
  dbName: string;
  ops: OpsType;
}

export interface RomaneioAntecipacaoViz {
  filterDate: string;
  dbName: string;
  ops: OpsType;
}

// ─── Bloqueio de entregadores (bloqueio_entregador) ──────────────────────────
// O cliente bloqueia o CPF/CNPJ inteiro na própria ops e só desbloqueia o que ele
// mesmo bloqueou; bloqueio do gestor aparece, mas não pode ser desfeito aqui.

export type BloqueioOrigem = 'gestor' | 'cliente';
export type BloqueioStatusFiltro = 'vigente' | 'revogado' | 'todos';

export interface BloqueioMotivo {
  codigo: string;
  rotulo: string;
}

export interface BloquearEntregadoresParams {
  documentos: string[];
  acao: 'bloquear' | 'desbloquear';
  motivo_codigo?: string;
  motivo_texto?: string;
  dbName: string;
  ops: OpsType;
}

export interface BloquearEntregadoresResult {
  success: string[];
  failed: { documento: string; reason: string }[];
  avisos: { documento: string; reason: string }[];
}

export interface BloqueiosListParams {
  dbName: string;
  ops: string;
  page?: number;
  limit?: number;
  filters?: {
    status?: BloqueioStatusFiltro;
    origem?: BloqueioOrigem;
    documento?: string;
  };
}

export interface BloqueioEntregador {
  id: number;
  numero_documento: string;
  origem: BloqueioOrigem;
  motivo_codigo: string | null;
  motivo_texto: string | null; // só nos bloqueios do cliente
  criado_por: string | null; // só nos bloqueios do cliente
  criado_em: string;
  revogado_em: string | null;
  revogado_por: string | null;
  revogado_origem: BloqueioOrigem | null;
  nome_completo: string | null;
  pode_desbloquear: boolean;
}

export interface BloqueiosListResponse {
  success: boolean;
  items: BloqueioEntregador[];
  motivos: BloqueioMotivo[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface EntregoFechamentoParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentWeek?: boolean;
}

export interface MuveFechamentoParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentWeek?: boolean;
}

export interface ImileFechamentoParams {
  ops: string;
  /** Primeiro dia da quinzena (YYYY-MM-01 ou YYYY-MM-16). Sem ele, o backend usa a última quinzena fechada. */
  inicio?: string;
  /** Deve ser true quando inicio for a quinzena atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentQuinzena?: boolean;
}

export interface EntregoSaldoParams {
  ops: string;
  /** Busca por telefone (apenas dígitos) */
  telefone?: string;
  /** Busca por CPF (11 dígitos) */
  cpf?: string;
  /** Busca por CNPJ (14 dígitos) */
  cnpj?: string;
  dbName: string;
}

export interface EntregoNFSListParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). O backend rejeita sem esse campo. */
  allowCurrentWeek?: boolean;
}

export interface EntregoNFSZipParams {
  ops: string;
  segunda?: string;
  allowCurrentWeek?: boolean;
}

export interface EntregoNFSZipStatusParams {
  ops: string;
  /** Chave S3 devolvida por gestorEntregoNFSZip, ex.: "nfs-zip/OPS/2025-01-06_2025-01-12.zip" */
  jobKey: string;
}

export interface EntregoSRListParams {
  ops: string;
  segunda?: string;
  /** Deve ser true quando segunda for a semana atual (em aberto). */
  allowCurrentWeek?: boolean;
}

export const romaneioService = {
  // Get saldo remanescente for an entregador by CPF/CNPJ
  async getSaldoEntrego(params: EntregoSaldoParams): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoSaldo', params);
  },

  // Get saldo da quinzena atual for an entregador iMile by CPF/CNPJ/telefone
  async getSaldoImile(params: EntregoSaldoParams): Promise<ApiResponse> {
    return apiClient.post('/imileSaldo', params);
  },

  // Get entrego weekly closing data (resumo + operacoes)
  async getEntregoFechamento(
    params: EntregoFechamentoParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoFechamento', params);
  },

  // Get muve weekly closing data (resumo + operacoes)
  async getMuveFechamento(
    params: MuveFechamentoParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorMuveFechamento', params);
  },

  // Get iMile fortnightly closing data (resumo + operacoes)
  async getImileFechamento(
    params: ImileFechamentoParams
  ): Promise<ApiResponse> {
    return apiClient.post('/imileFechamento', params);
  },

  // Get InDrive closing data grouped by vencimento (sexta-feira)
  async getIndriveFechamento(
    params: { ops: string; sexta?: string; allowOpenCycle?: boolean }
  ): Promise<ApiResponse> {
    return apiClient.post('/indriveFechamento', params);
  },

  // Get NFS list for the week (antecipacoes + saldos remanescentes)
  async getEntregoNFSList(
    params: EntregoNFSListParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoNFSList', params);
  },

  // Request async ZIP generation — returns { jobKey } immediately
  async getEntregoNFSZip(
    params: EntregoNFSZipParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoNFSZip', params);
  },

  // Check if the async ZIP job is ready; returns presigned URL if so
  async getEntregoNFSZipStatus(
    params: EntregoNFSZipStatusParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoNFSZipStatus', params);
  },

  // Get SR (Saldo Remanescente) list for the week
  async getEntregoSRList(
    params: EntregoSRListParams
  ): Promise<ApiResponse> {
    return apiClient.post('/gestorEntregoSRList', params);
  },

  // View delivery personnel registration data
  async getCadastroView(params: RomaneioCadastroView): Promise<ApiResponse> {
    return apiClient.post('/romaneioCadastroView', params);
  },

  // Get checkout summary for deliveries
  async getCheckoutSummary(
    params: RomaneioCheckoutSummary
  ): Promise<ApiResponse> {
    return apiClient.post('/romaneioCheckoutSummary', params);
  },

  // Submit checkout (freeze records and insert into romaneio_checkout)
  async submitCheckout(params: RomaneioCheckoutSubmit): Promise<ApiResponse> {
    return apiClient.post('/romaneioCheckoutSubmmit', params);
  },

  // Get delivery status for a specific person
  async getStatusPerson(params: RomaneioStatusPerson): Promise<ApiResponse> {
    return apiClient.post('/romaneioStatusPerson', params);
  },

  // Download TED transfer data
  async downloadTED(params: RomaneioTEDDownload): Promise<ApiResponse> {
    return apiClient.post('/romaneioTEDDownload', params);
  },

  // Get payment summary for deliveries
  async getPaymentSummary(
    params: RomaneioPaymentSummary
  ): Promise<ApiResponse> {
    return apiClient.post('/romaneioPaymentSummary', params);
  },

  // Get delivery statistics
  async getStatistics(params: RomaneioStatistics): Promise<ApiResponse> {
    return apiClient.post('/romaneioStatistics', params);
  },

  // Get advance payment visualization
  async getAntecipacaoViz(
    params: RomaneioAntecipacaoViz
  ): Promise<ApiResponse> {
    return apiClient.post('/romaneioAntecipacaoViz', params);
  },

  // Bloqueia/desbloqueia um ou vários entregadores na ops do cliente
  async bloquearEntregadores(
    params: BloquearEntregadoresParams
  ): Promise<ApiResponse<BloquearEntregadoresResult>> {
    return apiClient.post('/entregoCadastroBulkUpdate', params);
  },

  async listarBloqueios(
    params: BloqueiosListParams
  ): Promise<ApiResponse<BloqueiosListResponse>> {
    return apiClient.post('/romaneioCadastroBlockList', params);
  },
};
